<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import SendIcon from 'vue-material-design-icons/Send.vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import { resizeAutoGrowTextarea } from '@/lib/autoGrowTextarea'

const props = withDefaults(defineProps<{
  expanded: boolean
  isStreaming: boolean
  canSend: boolean
  disabled?: boolean
  readonly?: boolean
  /**
   * `default` keeps the legacy vertical layout (textarea block + footer row).
   * `inline` renders a self-contained input: a `#leading` control and the send
   * button flank a single-line textarea that grows/stacks as it fills.
   */
  variant?: 'default' | 'inline'
  /** Inline mode only: textarea content (use `v-model`). */
  modelValue?: string
  /** Inline mode only: textarea placeholder. */
  placeholder?: string
  /** Inline mode only: max rows the textarea grows to before scrolling. */
  maxLines?: number
  /** Remeasure inline layout when consumer typography changes or finishes loading. */
  layoutKey?: string | number
  /** Opt in to an idle translucent treatment; opaque remains the default. */
  idleTranslucent?: boolean
  /** Consumer-computed content state that keeps the frame opaque while filled. */
  filled?: boolean
}>(), {
  variant: 'default',
  modelValue: '',
  placeholder: '',
  maxLines: 5,
  idleTranslucent: false,
  filled: false,
})

const emit = defineEmits<{
  'update:expanded': [value: boolean]
  'update:modelValue': [value: string]
  send: []
  stop: []
  focus: []
  keydown: [event: KeyboardEvent]
}>()

/* ── Inline layout (variant === 'inline') ──────────────────────────────────
   The textarea sits between a leading control and the send button. While the
   text fits on one line it flows inline (controls absolutely pinned to the
   bottom-left/right, the textarea padded to clear them). Once the text would
   reach the send button it switches to a stacked layout: full-width textarea
   growing upward, with the controls on their own row below.

   Geometry is measured, not hardcoded: we read the live widths of the leading
   and action controls so the reserve adapts to whatever the slot holds. */
const textareaRef = ref<HTMLTextAreaElement | null>(null)
const leadingRef = ref<HTMLElement | null>(null)
const actionRef = ref<HTMLElement | null>(null)
const stacked = ref(false)

// px gap kept between a control and the inline text.
const GAP = 8
// Base horizontal padding of the textarea (captured once from CSS before we
// start overriding padding inline). The inline reserve is added on top of it.
let basePadX = -1

let measureCtx: CanvasRenderingContext2D | null = null
let measureCtxResolved = false
let resizeObserver: ResizeObserver | null = null

function getMeasureCtx() {
  if (!measureCtxResolved) {
    measureCtx = document.createElement('canvas').getContext('2d')
    measureCtxResolved = true
  }
  return measureCtx
}

// Decide layout (inline vs stacked) from the text's natural single-line width,
// apply the matching padding/margin, then grow height to content up to maxLines.
function recomputeComposer() {
  const ta = textareaRef.value
  if (!ta || props.variant !== 'inline') return

  const cs = getComputedStyle(ta)
  if (basePadX < 0) basePadX = parseFloat(cs.paddingLeft) || 12

  const text = props.modelValue ?? ''
  const leadingW = leadingRef.value?.offsetWidth ?? 0
  const actionW = actionRef.value?.offsetWidth ?? 0
  // The controls sit in a fixed-height band (the single-line textarea height).
  // When stacked we reserve a band of the same height below the text so the
  // centered controls keep their row. Fall back to the tallest control.
  const rowH = Math.max(
    parseFloat(cs.minHeight) || 0,
    leadingRef.value?.offsetHeight ?? 0,
    actionRef.value?.offsetHeight ?? 0,
  )

  // 1) Does the text fit on a single inline line, both controls beside it?
  //    clientWidth is the border-box inner width and is independent of how the
  //    padding is currently distributed, so this is stable across both states.
  let fitsOneLine = !text.includes('\n')
  const ctx = getMeasureCtx()
  if (fitsOneLine && ctx) {
    const inlineAvailable = ta.clientWidth - basePadX * 2 - leadingW - actionW - GAP * 2
    ctx.font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`
    fitsOneLine = ctx.measureText(text).width <= inlineAvailable
  }
  stacked.value = !fitsOneLine

  // 2) Apply padding/margin for the new state, then size to content. Done in the
  //    same tick after the state settles so only the final height paints.
  nextTick(() => {
    const el = textareaRef.value
    if (!el) return

    if (stacked.value) {
      el.style.paddingLeft = `${basePadX}px`
      el.style.paddingRight = `${basePadX}px`
      el.style.marginBottom = `${rowH}px`
    } else {
      el.style.paddingLeft = `${basePadX + leadingW + GAP}px`
      el.style.paddingRight = `${basePadX + actionW + GAP}px`
      el.style.marginBottom = '0px'
    }

    const next = getComputedStyle(el)
    const lineHeight = parseFloat(next.lineHeight) || (parseFloat(next.fontSize) || 16) * 1.5
    const padY = (parseFloat(next.paddingTop) || 0) + (parseFloat(next.paddingBottom) || 0)
    const maxHeight = (props.maxLines ?? 5) * lineHeight + padY
    el.style.maxHeight = `${maxHeight}px`
    resizeAutoGrowTextarea(el, maxHeight)
  })
}

function onInput(event: Event) {
  emit('update:modelValue', (event.target as HTMLTextAreaElement).value)
}

function onKeydown(event: KeyboardEvent) {
  emit('keydown', event)
  if (event.defaultPrevented) return
  if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
    event.preventDefault()
    if (!props.isStreaming && props.canSend && !props.disabled) emit('send')
  }
}

function focus() {
  textareaRef.value?.focus()
}

watch([() => props.modelValue, () => props.layoutKey], () => recomputeComposer(), { flush: 'post' })

onMounted(() => {
  if (props.variant !== 'inline') return
  recomputeComposer()
  if (textareaRef.value && typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => recomputeComposer())
    resizeObserver.observe(textareaRef.value)
  }
})

onBeforeUnmount(() => resizeObserver?.disconnect())

defineExpose({ focus, textareaRef })
</script>

<template>
  <div
    class="composer-frame"
    :class="{
      'composer-frame--elevated': expanded,
      'composer-frame--idle-translucent': idleTranslucent,
      'composer-frame--filled': filled,
    }"
  >
    <!-- Expansion panel: animates open/closed; only renders when slot is provided -->
    <Transition name="composer-expansion">
      <div v-if="expanded && $slots['expansion']" class="composer-expansion">
        <div class="composer-expansion__inner">
          <slot name="expansion" />
        </div>
      </div>
    </Transition>

    <!-- Inline layout: leading control + textarea + send all share one row -->
    <div v-if="variant === 'inline'" class="composer-inline">
      <textarea
        ref="textareaRef"
        :value="modelValue"
        :readonly="readonly || disabled"
        rows="1"
        :placeholder="placeholder"
        class="composer-inline__input"
        :class="{ 'composer-inline__input--stacked': stacked }"
        @input="onInput"
        @keydown="onKeydown"
        @focus="$emit('focus')"
      />

      <div v-if="$slots['leading']" ref="leadingRef" class="composer-inline__leading">
        <slot name="leading" />
      </div>

      <div ref="actionRef" class="composer-inline__action">
        <button
          v-if="isStreaming"
          type="button"
          class="composer-action composer-action--stop"
          aria-label="Stop"
          @click="$emit('stop')"
        >
          <svg class="composer-action__icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M7.5 6h9A1.5 1.5 0 0 1 18 7.5v9a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 6 16.5v-9A1.5 1.5 0 0 1 7.5 6Z" />
          </svg>
        </button>
        <BaseButton
          v-else
          type="button"
          variant="icon"
          size="icon"
          color="primary"
          :disabled="disabled || !canSend"
          aria-label="Send"
          @click="$emit('send')"
        >
          <template #icon>
            <SendIcon :size="18" aria-hidden="true" />
          </template>
        </BaseButton>
      </div>
    </div>

    <!-- Default layout: triggers, slotted input block, then a footer row -->
    <template v-else>
      <!-- Triggers: chips that toggle the expansion panel -->
      <div v-if="$slots['triggers']" class="composer-triggers">
        <slot name="triggers" />
      </div>

      <!-- Primary input area -->
      <slot />

      <!-- Meta row: optional controls between input and footer -->
      <div v-if="$slots['meta']" class="composer-meta">
        <slot name="meta" />
      </div>

      <!-- Footer -->
      <div class="composer-footer">
        <div v-if="$slots['footer-leading']">
          <slot name="footer-leading" />
        </div>

        <button
          v-if="isStreaming"
          type="button"
          class="composer-action composer-action--stop"
          aria-label="Stop"
          @click="$emit('stop')"
        >
          <svg class="composer-action__icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M7.5 6h9A1.5 1.5 0 0 1 18 7.5v9a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 6 16.5v-9A1.5 1.5 0 0 1 7.5 6Z" />
          </svg>
        </button>
        <button
          v-else
          type="button"
          class="composer-action composer-action--send"
          :disabled="disabled || !canSend"
          aria-label="Send"
          @click="$emit('send')"
        >
          <SendIcon :size="18" aria-hidden="true" />
        </button>
      </div>
    </template>
  </div>
</template>

<style scoped>
.composer-frame {
  overflow: hidden;
  border: 1px solid var(--composer-border, var(--theme-border, rgba(255, 255, 255, 0.1)));
  border-radius: var(--composer-radius, 10px);
  background: var(--composer-bg, var(--theme-surface-solid, rgba(9, 12, 16, 1)));
  box-shadow: var(--composer-shadow, 0 8px 22px rgba(0, 0, 0, 0.18));
  transition: background-color 300ms ease, border-color 300ms ease, box-shadow 300ms ease;
}

.composer-frame--elevated {
  border-color: var(--composer-border-elevated, color-mix(in srgb, var(--theme-primary, rgb(29 138 255)) 30%, var(--theme-border, rgba(255, 255, 255, 0.1))));
  box-shadow: var(--composer-shadow-elevated, 0 8px 22px color-mix(in srgb, var(--theme-primary, rgb(29 138 255)) 8%, transparent));
}

.composer-frame--idle-translucent {
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
}

/* Opaque is the base state; idle is the exception. :focus-within covers the
   textarea, chevron, and send button without JS focus plumbing. */
.composer-frame--idle-translucent:not(:focus-within):not(.composer-frame--elevated):not(.composer-frame--filled) {
  background: var(
    --composer-bg-idle,
    color-mix(in srgb, var(--theme-surface-solid, rgba(9, 12, 16, 1)) 50%, transparent)
  );
  border-color: var(
    --composer-border-idle,
    color-mix(in srgb, var(--theme-border, rgba(255, 255, 255, 0.1)) 60%, transparent)
  );
  box-shadow: none;
}

.composer-triggers {
  padding: 0.75rem 0.75rem 0;
}

.composer-expansion {
  display: grid;
  grid-template-rows: 1fr;
  border-bottom: 1px solid var(--composer-border, var(--theme-border, rgba(255, 255, 255, 0.1)));
}

.composer-expansion__inner {
  overflow: hidden;
  min-height: 0;
}

.composer-meta {
  /* consumers style this */
}

.composer-footer {
  display: flex;
  align-items: center;
  padding: 0.625rem;
  gap: 0.5rem;
}

.composer-footer > div {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Inline layout: a positioning context so leading + action can flank the text.
   `flow-root` makes this a BFC so the textarea's stacked margin-bottom reserve is
   contained here — keeping the absolutely-pinned controls in that bottom row
   instead of letting the margin collapse out and ride up with the text. */
.composer-inline {
  position: relative;
  display: flow-root;
}

.composer-inline__input {
  display: block;
  width: 100%;
  box-sizing: border-box;
  min-height: var(--composer-row-height, 3rem);
  outline: 0;
  resize: none;
  overflow-y: auto;
  padding: 0.7rem 0.75rem;
  background: transparent;
  border: 0;
  color: var(--composer-input-color, var(--theme-on-surface, #fff));
  font: var(--composer-input-font, 400 1rem/1.5 inherit);
  /* Animate layout reserves and fallback height changes without collapsing
     the focused field during measurement. */
  transition: height 140ms ease-out, padding 140ms ease-out, margin-bottom 140ms ease-out;
}

.composer-inline__input::placeholder {
  color: var(--composer-input-placeholder, var(--theme-on-surface-variant, rgba(255, 255, 255, 0.4)));
}

/* Leading + action each live in a row-height band pinned to the composer's
   bottom edge — the single-line height when inline, the reserved strip when
   stacked — and center their contents within it. `--composer-row-height` is the
   single vertical-room knob, and the band centers any control regardless of size. */
.composer-inline__leading,
.composer-inline__action {
  position: absolute;
  bottom: 0;
  height: var(--composer-row-height, 3rem);
  display: flex;
  align-items: center;
}

.composer-inline__leading {
  left: 0.625rem;
}

.composer-inline__action {
  right: 0.625rem;
}

@media (prefers-reduced-motion: reduce) {
  .composer-inline__input {
    transition: none;
  }
}

.composer-action {
  display: inline-flex;
  margin-left: auto;
  flex-shrink: 0;
  width: var(--composer-action-size, 2rem);
  height: var(--composer-action-size, 2rem);
  align-items: center;
  justify-content: center;
  border-radius: var(--composer-action-radius, 10px);
  border: 0;
  cursor: pointer;
  transition: opacity 150ms ease, transform 150ms ease, background-color 150ms ease;
}

.composer-action:disabled {
  cursor: not-allowed;
  opacity: 0.4;
}

.composer-action:not(:disabled):active {
  transform: scale(0.96);
}

.composer-action--send {
  background: var(--composer-primary, var(--theme-primary, rgb(29 138 255)));
  color: var(--composer-on-primary, var(--theme-on-primary, white));
}

.composer-action--send:not(:disabled):hover {
  opacity: 0.9;
}

.composer-action--stop {
  background: var(--composer-danger, #ef4444);
  color: white;
}

.composer-action--stop:not(:disabled):hover {
  opacity: 0.92;
}

.composer-action__icon {
  width: 1rem;
  height: 1rem;
}

/* Expansion transition — height shrink + fade so the footer collapses smoothly */
.composer-expansion-enter-active,
.composer-expansion-leave-active {
  transition:
    grid-template-rows 220ms ease,
    opacity 180ms ease,
    border-color 180ms ease;
}

.composer-expansion-enter-from,
.composer-expansion-leave-to {
  grid-template-rows: 0fr;
  opacity: 0;
  border-bottom-color: transparent;
}
</style>
